/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Box,
  Typography,
} from '@mui/material';
import { Formik, FieldArray } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectNotification } from 'modules/common/notifications/selectors';
import { selectOrganizationId } from 'modules/common/auth/selectors';
import { notificationActions } from 'modules/common/notifications/slice';
import { addScenarioValidation } from 'modules/scenario/validation/scenario-form-validation';
import ERROR_TYPES from 'modules/common/constants/error-types';
import TOAST_TYPES from 'modules/common/constants/toast-types';
import { scenarioActions } from 'modules/scenario/slice';
import getRandomId from 'modules/scenario/function';
import { selectVisuals } from 'modules/scenario/selectors';
import { Alert, TextField } from '../style';
import VisualSearch from '../visual-search';
/**
 * Form to create scenario
 * @param {*} param0
 * @returns
 */
const AddScenarioForm = ({
  open,
  onClose,
  setSelectedVisual,
  setFilterModalOpen,
  setFilters,
  visualFilters,
}) => {
  const dispatch = useDispatch();
  //
  const notification = useSelector(selectNotification);
  const organizationId = useSelector(selectOrganizationId);
  const visuals = useSelector(selectVisuals);
  //
  const modalAction = (action) => {
    if (notification?.isEnabled) dispatch(notificationActions.resetNotification());
    onClose(action);
  };
  //
  useEffect(() => {
    if (notification?.isEnabled && notification?.type === ERROR_TYPES.SUCCESS) {
      toast(notification?.message, { type: TOAST_TYPES.SUCCESS });
      modalAction(false);
    }
  }, [notification]);
  const initialValues = {
    name: '',
    description: '',
    visuals: [
      {
        id: getRandomId(),
        displayName: '',
        filters: null,
      },
    ],
  };
  //
  const onSubmitAddScenario = async (values) => {
    const visualsWithFilters = values.visuals.map((visual) => {
      const filters = visualFilters[visual.id];

      if (!filters) {
        toast(`Filter is missing for visual: ${visual.displayName}`, { type: TOAST_TYPES.ERROR });
        throw new Error(`Filter is missing for visual: ${visual.displayName}`);
      }

      return {
        displayName: visual?.displayName,
        filters,
      };
    });

    const valueObj = {
      name: values.name,
      description: values.description,
      visuals: visualsWithFilters,
    };

    dispatch(scenarioActions.createScenarios({ ...valueObj, organizationId }));
  };
  //
  const updateVisualName = (oldName, newName) => {
    setFilters((prev) => {
      const updated = { ...prev };

      if (updated[oldName]) {
        updated[newName] = updated[oldName];
        delete updated[oldName];
      }

      return updated;
    });
  };
  //
  const closeAddScenarioModal = () => {
    setFilters();
    modalAction(false);
  };
  //
  return (
    <Dialog open={open} onClose={closeAddScenarioModal} maxWidth="md" fullWidth>
      <DialogTitle>Add Scenario</DialogTitle>
      <DialogContent>
        <Formik
          initialValues={initialValues}
          validationSchema={addScenarioValidation}
          onSubmit={onSubmitAddScenario}
        >
          {({
            handleSubmit,
            handleBlur,
            handleChange,
            setFieldValue,
            isSubmitting,
            touched,
            values,
            errors,
          }) => {
            const visualsInvalid =
              !values.visuals?.length || values.visuals.some((v) => !v.displayName?.trim());
            return (
              <form noValidate onSubmit={handleSubmit}>
                {notification?.isEnabled && notification?.type === ERROR_TYPES.ERROR && (
                  <Alert mt={2} mb={3} severity={notification?.type}>
                    {notification?.message}
                  </Alert>
                )}
                <TextField
                  name="name"
                  value={values.name}
                  error={Boolean(touched.name && errors.name)}
                  helperText={touched.name && errors.name}
                  fullWidth
                  onBlur={handleBlur}
                  onChange={handleChange}
                  my={2}
                  label="Scenario Name"
                  size="small"
                />
                <TextField
                  name="description"
                  value={values.description}
                  error={Boolean(touched.description && errors.description)}
                  helperText={touched.description && errors.description}
                  fullWidth
                  onBlur={handleBlur}
                  onChange={handleChange}
                  my={2}
                  label="Description"
                  size="small"
                />
                <Typography>Visuals</Typography>
                <FieldArray name="visuals">
                  {({ push, remove }) => (
                    <>
                      {values.visuals.map((visual, index) => (
                        <Box
                          key={visual.id}
                          sx={{ mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}
                        >
                          <VisualSearch
                            value={visual?.displayName}
                            visuals={visuals}
                            error={Boolean(
                              errors.visuals?.[index]?.displayName &&
                                touched.visuals?.[index]?.displayName
                            )}
                            helperText={
                              touched.visuals?.[index]?.displayName &&
                              errors.visuals?.[index]?.displayName
                            }
                            onChange={(value) => {
                              updateVisualName(visual.displayName, value);
                              setFieldValue(`visuals.${index}.displayName`, value);
                            }}
                          />
                          <Box my={3}>
                            <Button
                              color="info"
                              variant="contained"
                              disabled={!visual?.displayName}
                              onClick={() => {
                                setSelectedVisual(visual);
                                setFilterModalOpen(true);
                              }}
                            >
                              Add Filters
                            </Button>
                          </Box>

                          <Button
                            color="error"
                            onClick={() => {
                              remove(index);
                              //
                              setFilters((prevFilters) => {
                                const updated = { ...prevFilters };
                                delete updated[visual?.displayName];
                                //
                                return updated;
                              });
                            }}
                          >
                            Remove
                          </Button>
                        </Box>
                      ))}

                      <Button
                        variant="outlined"
                        disabled={visualsInvalid}
                        onClick={() =>
                          push({
                            id: getRandomId(),
                            displayName: '',
                            filters: '',
                          })
                        }
                      >
                        Add Visual
                      </Button>
                    </>
                  )}
                </FieldArray>
                <DialogActions>
                  <Button onClick={closeAddScenarioModal} variant="contained" color="primary">
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="success"
                    disabled={isSubmitting || visualsInvalid}
                  >
                    Save
                  </Button>
                </DialogActions>
              </form>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};
//
export default AddScenarioForm;
